import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, retry, throwError, timer, timeout } from 'rxjs';
import { environment } from 'environments/environment';
import { PosthogService } from '../services/posthog.service';

/** Default when `environment.httpRequestTimeoutMs` is missing or invalid. */
const DEFAULT_HTTP_TIMEOUT_MS = 30_000;

function resolveTimeoutMs(raw: unknown): number {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
        return DEFAULT_HTTP_TIMEOUT_MS;
    }
    return n;
}

function isTimeoutError(err: unknown): boolean {
    return err !== null && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'TimeoutError';
}

function isTransientForRetry(err: unknown): boolean {
    if (isTimeoutError(err)) {
        return true;
    }
    if (err instanceof HttpErrorResponse) {
        const s = err.status;
        return s === 0 || s === 502 || s === 503 || s === 504;
    }
    return false;
}

/**
 * Timeouts all requests; retries safe idempotent GETs on transient failures only.
 * Runs after Auth0's interceptor (register second in `withInterceptors`).
 */
export const httpResilienceInterceptor: HttpInterceptorFn = (req, next) => {
    const posthogService = inject(PosthogService);
    const startedAt = Date.now();
    const timeoutMs = resolveTimeoutMs(environment.httpRequestTimeoutMs);
    const base$ = next(req).pipe(timeout(timeoutMs));

    const withRetry$ =
        req.method === 'GET'
            ? base$.pipe(
                  retry({
                      count: 2,
                      delay: (error, retryCount) =>
                          isTransientForRetry(error) ? timer(400 * retryCount) : throwError(() => error)
                  })
              )
            : base$;

    return withRetry$.pipe(
        catchError((err: unknown) => {
            const normalizedError = isTimeoutError(err)
                ? new HttpErrorResponse({
                    status: 0,
                    statusText: 'Request Timeout',
                    url: req.url,
                    error: { message: 'The request took too long. Check your connection and try again.' }
                })
                : err;

            if (normalizedError instanceof HttpErrorResponse) {
                const retryAttempts = req.method === 'GET' && isTransientForRetry(normalizedError) ? 2 : 0;
                posthogService.captureApiFailure(
                    normalizedError,
                    { method: req.method, url: req.url },
                    Date.now() - startedAt,
                    retryAttempts,
                );
            }

            return throwError(() => normalizedError);
        })
    );
};
